import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Paper,
  Typography,
  IconButton,
  Box,
  TextField,
  Button,
  Slider,
  Popover,
  Chip,
  InputAdornment,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Close as CloseIcon,
  DragIndicator,
  Edit as EditIcon,
  FormatColorFill as FormatColorFillIcon,
  TextFields as TextFieldsIcon,
  Palette as PaletteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useCorkboard, TOOL_MODES } from './CorkboardProvider';
import useThemeStyles from '../../hooks/useThemeStyles';
import corkboardColors from './corkboardColors.json';

const { noteColors, textColors, defaults: colorDefaults } = corkboardColors;

// Get array of color keys for the picker UI
const NOTE_COLOR_KEYS = Object.keys(noteColors);
const TEXT_COLOR_KEYS = Object.keys(textColors);

// Predefined categories for notes
const NOTE_CATEGORIES = [
  'General',
  'Character',
  'Plot',
  'Setting',
  'Research',
  'Worldbuilding',
  'Dialogue',
  'Theme',
  'Conflict',
  'Other',
];

/**
 * Resolve a color key to its actual hex value based on theme
 * Falls back to treating the value as a raw hex color for backwards compatibility
 */
const resolveColor = (colorKey, colorMap, isDarkMode) => {
  if (!colorKey) return null;
  const colorEntry = colorMap[colorKey];
  if (colorEntry) {
    return isDarkMode ? colorEntry.dark : colorEntry.light;
  }
  // Fallback: if it's already a hex color, return as-is (backwards compatibility)
  if (colorKey.startsWith('#') || colorKey.startsWith('rgb')) {
    return colorKey;
  }
  return null;
};

/**
 * CorkboardNote - A draggable, resizable note on the corkboard canvas
 */
const CorkboardNote = ({ note }) => {
  const { colors, isDark } = useThemeStyles();
  const { moveNote, updateNote, deleteNote, resizeNote, finalizeNoteMove, toolMode, viewState } =
    useCorkboard();

  // Get project chapters for autocomplete
  const activeProject = useSelector((state) => state.projects.activeProject);
  const projectChapters = useMemo(() => {
    const chapters = activeProject?.thinChapters || [];
    return chapters.map((ch) => ch.name || ch.title || `Chapter ${ch.id}`);
  }, [activeProject?.thinChapters]);

  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(note.title || '');
  const [editContent, setEditContent] = useState(note.content || '');
  const [editCategory, setEditCategory] = useState(note.category || 'General');
  const [previewSize, setPreviewSize] = useState(null);
  const [formatMenuAnchor, setFormatMenuAnchor] = useState(null);
  const [colorMenuAnchor, setColorMenuAnchor] = useState(null);
  const [textColorMenuAnchor, setTextColorMenuAnchor] = useState(null);
  const [newChapter, setNewChapter] = useState('');
  const [newCharacter, setNewCharacter] = useState('');

  const dragStartPos = useRef({ x: 0, y: 0 });
  const noteStartPos = useRef({ x: 0, y: 0 });
  const resizeStartPos = useRef({ x: 0, y: 0 });
  const noteStartSize = useRef({ width: 0, height: 0 });
  const noteRef = useRef(null);

  const noteId = note._id || note.id || note.noteId;

  // Sync edit state when note changes
  useEffect(() => {
    if (!isEditing) {
      setEditTitle(note.title || '');
      setEditContent(note.content || '');
      setEditCategory(note.category || 'General');
      setNewChapter('');
      setNewCharacter('');
    }
  }, [note.title, note.content, note.category, isEditing]);

  // Handle clicks outside the note to cancel editing
  useEffect(() => {
    if (!isEditing) return;

    const handleClickOutside = (event) => {
      // Check if click is outside the note
      if (noteRef.current && !noteRef.current.contains(event.target)) {
        // Don't cancel if clicking on a popover, menu, or autocomplete dropdown
        const { target } = event;
        if (
          target.closest('.MuiPopover-root') ||
          target.closest('.MuiMenu-root') ||
          target.closest('.MuiModal-root') ||
          target.closest('.MuiAutocomplete-popper') ||
          target.closest('.MuiAutocomplete-listbox') ||
          target.closest('.MuiAutocomplete-option')
        ) {
          return;
        }
        setIsEditing(false);
        setNewChapter('');
        setNewCharacter('');
      }
    };

    // Add listener with a small delay to avoid immediate trigger
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    // eslint-disable-next-line consistent-return
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditing]);

  const startEditing = useCallback(() => {
    if (toolMode !== TOOL_MODES.CURSOR) return;
    setIsEditing(true);
    setEditTitle(note.title || '');
    setEditContent(note.content || '');
    setEditCategory(note.category || 'General');
  }, [toolMode, note.title, note.content, note.category]);

  const handleContentClick = useCallback(
    (e) => {
      // Only start editing on single click if not already editing and in cursor mode
      if (!isEditing && toolMode === TOOL_MODES.CURSOR) {
        e.stopPropagation();
        startEditing();
      }
    },
    [isEditing, toolMode, startEditing]
  );

  const handleHeaderMouseDown = useCallback(
    (e) => {
      // Don't start drag if not in cursor mode or if editing
      if (toolMode !== TOOL_MODES.CURSOR || isEditing) return;

      // Don't drag if clicking on buttons
      if (e.target.tagName === 'BUTTON' || e.target.closest('button')) {
        return;
      }

      e.stopPropagation();
      e.preventDefault();
      setIsDragging(true);

      dragStartPos.current = { x: e.clientX, y: e.clientY };
      noteStartPos.current = { x: note.x, y: note.y };

      let rafId = null;
      const handleMouseMove = (moveEvent) => {
        // Cancel any pending animation frame
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }

        // Throttle updates using requestAnimationFrame
        rafId = requestAnimationFrame(() => {
          const dx = (moveEvent.clientX - dragStartPos.current.x) / viewState.zoom;
          const dy = (moveEvent.clientY - dragStartPos.current.y) / viewState.zoom;

          // Skip attached drawing updates during drag for performance
          moveNote(noteId, noteStartPos.current.x + dx, noteStartPos.current.y + dy, true);
          rafId = null;
        });
      };

      const handleMouseUp = () => {
        // Cancel any pending animation frame
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }

        setIsDragging(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);

        // Finalize attached drawings after drag ends
        if (finalizeNoteMove) {
          finalizeNoteMove(noteId);
        }
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [toolMode, isEditing, note.x, note.y, noteId, moveNote, viewState.zoom, finalizeNoteMove]
  );

  const handleDoubleClick = useCallback(
    (e) => {
      e.stopPropagation();
      startEditing();
    },
    [startEditing]
  );

  const handleSave = useCallback(() => {
    updateNote(noteId, {
      title: editTitle,
      content: editContent,
      category: editCategory,
      updatedEpoch: Date.now(),
    });
    setIsEditing(false);
    setNewChapter('');
    setNewCharacter('');
  }, [noteId, editTitle, editContent, editCategory, updateNote]);

  // Handle adding a chapter
  const handleAddChapter = useCallback(() => {
    if (newChapter.trim()) {
      const currentChapters = note.relatedChapters || [];
      // Avoid duplicates
      const chapterName = newChapter.trim();
      const exists = currentChapters.some(
        (ch) => (typeof ch === 'string' ? ch : ch.name || ch.title) === chapterName
      );
      if (!exists) {
        updateNote(noteId, {
          relatedChapters: [...currentChapters, chapterName],
          updatedEpoch: Date.now(),
        });
        setNewChapter('');
      }
    }
  }, [newChapter, note.relatedChapters, noteId, updateNote]);

  // Handle removing a chapter
  const handleRemoveChapter = useCallback(
    (index) => {
      const currentChapters = [...(note.relatedChapters || [])];
      currentChapters.splice(index, 1);
      updateNote(noteId, {
        relatedChapters: currentChapters,
        updatedEpoch: Date.now(),
      });
    },
    [note.relatedChapters, noteId, updateNote]
  );

  // Handle adding a character
  const handleAddCharacter = useCallback(() => {
    if (newCharacter.trim()) {
      const currentCharacters = note.relatedCharacters || [];
      // Avoid duplicates
      const characterName = newCharacter.trim();
      const exists = currentCharacters.some(
        (ch) => (typeof ch === 'string' ? ch : ch.name || ch.title) === characterName
      );
      if (!exists) {
        updateNote(noteId, {
          relatedCharacters: [...currentCharacters, characterName],
          updatedEpoch: Date.now(),
        });
        setNewCharacter('');
      }
    }
  }, [newCharacter, note.relatedCharacters, noteId, updateNote]);

  // Handle removing a character
  const handleRemoveCharacter = useCallback(
    (index) => {
      const currentCharacters = [...(note.relatedCharacters || [])];
      currentCharacters.splice(index, 1);
      updateNote(noteId, {
        relatedCharacters: currentCharacters,
        updatedEpoch: Date.now(),
      });
    },
    [note.relatedCharacters, noteId, updateNote]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        setIsEditing(false);
      } else if (e.key === 'Enter' && e.ctrlKey) {
        handleSave();
      }
    },
    [handleSave]
  );

  const handleDelete = useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      deleteNote(noteId);
    },
    [noteId, deleteNote]
  );

  const handleResizeMouseDown = useCallback(
    (e) => {
      // Don't start resize if not in cursor mode or if editing
      if (toolMode !== TOOL_MODES.CURSOR || isEditing) return;

      e.stopPropagation();
      e.preventDefault();
      setIsResizing(true);

      resizeStartPos.current = { x: e.clientX, y: e.clientY };
      noteStartSize.current = {
        width: note.width || 200,
        height: note.height || 150,
      };

      let finalSize = null;

      const handleMouseMove = (moveEvent) => {
        const dx = (moveEvent.clientX - resizeStartPos.current.x) / viewState.zoom;
        const dy = (moveEvent.clientY - resizeStartPos.current.y) / viewState.zoom;

        const newWidth = Math.max(150, noteStartSize.current.width + dx);
        const newHeight = Math.max(100, noteStartSize.current.height + dy);

        finalSize = { width: newWidth, height: newHeight };
        // Show preview
        setPreviewSize(finalSize);
      };

      const handleMouseUp = () => {
        if (finalSize) {
          // Apply the resize
          resizeNote(noteId, finalSize.width, finalSize.height);
        }
        setIsResizing(false);
        setPreviewSize(null);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [toolMode, isEditing, note.width, note.height, noteId, resizeNote, viewState.zoom]
  );

  // Memoize cursor styles to avoid recalculation
  const headerCursorStyle = useMemo(() => {
    if (toolMode !== TOOL_MODES.CURSOR) return 'default';
    if (isDragging) return 'grabbing';
    return 'grab';
  }, [toolMode, isDragging]);

  const contentCursorStyle = useMemo(() => {
    if (isEditing) return 'default';
    if (toolMode === TOOL_MODES.CURSOR) return 'text';
    return 'default';
  }, [isEditing, toolMode]);

  // Get current dimensions (preview or actual) - memoized
  const currentWidth = useMemo(
    () => previewSize?.width ?? note.width ?? 200,
    [previewSize?.width, note.width]
  );
  const currentHeight = useMemo(
    () => previewSize?.height ?? note.height ?? 150,
    [previewSize?.height, note.height]
  );

  // Calculate font size - scales with note size but can be manually adjusted
  // Base font size is calculated from note width (scales proportionally)
  // fontSizeMultiplier allows manual adjustment (default 1.0)
  // Memoize to avoid recalculation on every render
  const fontSizeMultiplier = useMemo(
    () => note.fontSizeMultiplier ?? 1.0,
    [note.fontSizeMultiplier]
  );
  const { calculatedFontSize } = useMemo(() => {
    const base = Math.max(10, Math.min(20, (currentWidth / 200) * 14));
    return {
      calculatedFontSize: base * fontSizeMultiplier,
    };
  }, [currentWidth, fontSizeMultiplier]);

  // Get colors with defaults - memoized and theme-aware (from corkboardColors.json)
  // Colors are stored as keys (e.g., "yellow") and resolved to hex based on theme
  const noteColorKey = note.color || colorDefaults.note;
  const textColorKey = note.textColor || colorDefaults.text;

  const noteColor = useMemo(
    () => resolveColor(noteColorKey, noteColors, isDark) || (isDark ? '#424242' : '#fef3c7'),
    [noteColorKey, isDark]
  );
  const textColor = useMemo(
    () =>
      resolveColor(textColorKey, textColors, isDark) ||
      (isDark ? 'rgba(255,255,255,0.87)' : 'rgba(0,0,0,0.87)'),
    [textColorKey, isDark]
  );

  // Handle font size change
  const handleFontSizeChange = useCallback(
    (event, newValue) => {
      updateNote(noteId, {
        fontSizeMultiplier: newValue,
        updatedEpoch: Date.now(),
      });
    },
    [noteId, updateNote]
  );

  // Handle background color change - stores the color KEY, not the hex value
  const handleColorChange = useCallback(
    (colorKey) => {
      updateNote(noteId, {
        color: colorKey,
        updatedEpoch: Date.now(),
      });
      setColorMenuAnchor(null);
    },
    [noteId, updateNote]
  );

  // Handle text color change - stores the color KEY, not the hex value
  const handleTextColorChange = useCallback(
    (colorKey) => {
      updateNote(noteId, {
        textColor: colorKey,
        updatedEpoch: Date.now(),
      });
      setTextColorMenuAnchor(null);
    },
    [noteId, updateNote]
  );

  return (
    <Paper
      ref={noteRef}
      elevation={isDragging || isResizing || isEditing ? 8 : 3}
      sx={{
        position: 'absolute',
        left: note.x,
        top: note.y,
        width: currentWidth,
        minHeight: currentHeight,
        backgroundColor: noteColor,
        transition: isDragging || isResizing ? 'none' : 'box-shadow 0.2s',
        zIndex: isDragging || isResizing || isEditing ? 1000 : note.zIndex || 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        userSelect: isEditing ? 'text' : 'none',
      }}
      onDoubleClick={handleDoubleClick}
      data-corkboard-note
    >
      {/* Header / Drag Handle */}
      <Box
        onMouseDown={handleHeaderMouseDown}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 0.5,
          backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          cursor: headerCursorStyle,
        }}
      >
        <DragIndicator
          sx={{ fontSize: 18, color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {toolMode === TOOL_MODES.CURSOR && !isEditing && (
            <>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setFormatMenuAnchor(e.currentTarget);
                }}
                sx={{
                  p: 0.25,
                  color: isDark ? 'rgba(255,255,255,0.7)' : 'inherit',
                  '&:hover': {
                    backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                  },
                }}
                title="Format"
              >
                <TextFieldsIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setColorMenuAnchor(e.currentTarget);
                }}
                sx={{
                  p: 0.25,
                  color: isDark ? 'rgba(255,255,255,0.7)' : 'inherit',
                  '&:hover': {
                    backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                  },
                }}
                title="Background Color"
              >
                <FormatColorFillIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setTextColorMenuAnchor(e.currentTarget);
                }}
                sx={{
                  p: 0.25,
                  color: isDark ? 'rgba(255,255,255,0.7)' : 'inherit',
                  '&:hover': {
                    backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                  },
                }}
                title="Text Color"
              >
                <PaletteIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </>
          )}
          <IconButton
            size="small"
            onClick={handleDelete}
            sx={{
              p: 0.25,
              color: isDark ? 'rgba(255,255,255,0.7)' : 'inherit',
              '&:hover': {
                backgroundColor: 'rgba(255,0,0,0.1)',
                color: 'error.main',
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box
        sx={{
          p: 1.5,
          flexGrow: 1,
          cursor: contentCursorStyle,
          minHeight: 100,
        }}
        onClick={handleContentClick}
        onDoubleClick={handleDoubleClick}
      >
        {isEditing ? (
          <Box onKeyDown={handleKeyDown} onClick={(e) => e.stopPropagation()}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              placeholder="Title"
              autoFocus
              sx={{ mb: 1.5 }}
              InputProps={{
                sx: {
                  fontWeight: 'bold',
                  fontSize: `${calculatedFontSize * 1.1}px`,
                  color: textColor,
                },
              }}
              onMouseDown={(e) => e.stopPropagation()}
            />
            <TextField
              fullWidth
              multiline
              minRows={4}
              maxRows={10}
              size="small"
              variant="outlined"
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              placeholder="Content..."
              sx={{ mb: 1.5 }}
              InputProps={{
                sx: {
                  fontSize: `${calculatedFontSize}px`,
                  color: textColor,
                },
              }}
              onMouseDown={(e) => e.stopPropagation()}
            />

            {/* Category Selector */}
            <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
              <InputLabel id="note-category-label">Category</InputLabel>
              <Select
                labelId="note-category-label"
                value={editCategory}
                label="Category"
                onChange={(e) => setEditCategory(e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
                sx={{
                  fontSize: `${calculatedFontSize * 0.9}px`,
                  color: textColor,
                }}
              >
                {NOTE_CATEGORIES.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Related Chapters Editor */}
            <Box sx={{ mb: 1.5 }}>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mb: 0.5,
                  fontWeight: 'bold',
                  color: textColor,
                  opacity: 0.7,
                  fontSize: `${calculatedFontSize * 0.85}px`,
                }}
              >
                Relevant Chapters
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                {(note.relatedChapters || []).map((chapter, index) => {
                  const chapterName =
                    typeof chapter === 'string'
                      ? chapter
                      : chapter.name || chapter.title || `Chapter ${index + 1}`;
                  return (
                    <Chip
                      key={`chapter-${chapterName}-${index}`} // eslint-disable-line react/no-array-index-key
                      label={chapterName}
                      size="small"
                      onDelete={() => handleRemoveChapter(index)}
                      sx={{
                        backgroundColor: `${textColor}15`,
                        color: textColor,
                        fontSize: `${calculatedFontSize * 0.75}px`,
                        '& .MuiChip-deleteIcon': {
                          color: textColor,
                          opacity: 0.7,
                          fontSize: '14px',
                        },
                      }}
                    />
                  );
                })}
              </Box>
              <Autocomplete
                freeSolo
                size="small"
                value={null}
                options={projectChapters.filter(
                  (ch) =>
                    !(note.relatedChapters || []).some((existing) => {
                      const existingName =
                        typeof existing === 'string' ? existing : existing.name || existing.title;
                      return existingName === ch;
                    })
                )}
                inputValue={newChapter}
                onInputChange={(event, value, reason) => {
                  // Only update input if user is typing, not when clearing after selection
                  if (reason === 'input' || reason === 'clear') {
                    setNewChapter(value || '');
                  }
                }}
                onChange={(event, value) => {
                  if (value) {
                    const currentChapters = note.relatedChapters || [];
                    const exists = currentChapters.some(
                      (ch) => (typeof ch === 'string' ? ch : ch.name || ch.title) === value
                    );
                    if (!exists) {
                      updateNote(noteId, {
                        relatedChapters: [...currentChapters, value],
                        updatedEpoch: Date.now(),
                      });
                    }
                  }
                  // Always clear the input after selection
                  setNewChapter('');
                }}
                renderInput={(params) => {
                  // Merge InputProps with custom endAdornment
                  const mergedInputProps = {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {params.InputProps.endAdornment}
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={handleAddChapter}
                            disabled={!newChapter.trim()}
                            sx={{ p: 0.5 }}
                          >
                            <AddIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </InputAdornment>
                      </>
                    ),
                  };
                  return (
                    <TextField
                      inputRef={params.inputRef}
                      inputProps={params.inputProps}
                      disabled={params.disabled}
                      fullWidth={params.fullWidth}
                      size={params.size}
                      placeholder="Search chapters..."
                      variant="outlined"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newChapter.trim()) {
                          e.preventDefault();
                          handleAddChapter();
                        }
                      }}
                      sx={{ fontSize: `${calculatedFontSize * 0.9}px` }}
                      InputProps={mergedInputProps} // eslint-disable-line react/jsx-no-duplicate-props
                      onMouseDown={(e) => e.stopPropagation()}
                    />
                  );
                }}
                onMouseDown={(e) => e.stopPropagation()}
              />
            </Box>

            {/* Related Characters Editor */}
            <Box sx={{ mb: 1.5 }}>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mb: 0.5,
                  fontWeight: 'bold',
                  color: textColor,
                  opacity: 0.7,
                  fontSize: `${calculatedFontSize * 0.85}px`,
                }}
              >
                Relevant Characters
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                {(note.relatedCharacters || []).map((character, index) => {
                  const characterName =
                    typeof character === 'string'
                      ? character
                      : character.name || character.title || `Character ${index + 1}`;
                  return (
                    <Chip
                      key={`character-${characterName}-${index}`} // eslint-disable-line react/no-array-index-key
                      label={characterName}
                      size="small"
                      onDelete={() => handleRemoveCharacter(index)}
                      sx={{
                        backgroundColor: `${textColor}15`,
                        color: textColor,
                        fontSize: `${calculatedFontSize * 0.75}px`,
                        '& .MuiChip-deleteIcon': {
                          color: textColor,
                          opacity: 0.7,
                          fontSize: '14px',
                        },
                      }}
                    />
                  );
                })}
              </Box>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                value={newCharacter}
                onChange={(e) => setNewCharacter(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddCharacter();
                  }
                }}
                placeholder="Add character..."
                sx={{ fontSize: `${calculatedFontSize * 0.9}px` }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={handleAddCharacter}
                        disabled={!newCharacter.trim()}
                        sx={{ p: 0.5 }}
                      >
                        <AddIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                onMouseDown={(e) => e.stopPropagation()}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setIsEditing(false)}
                sx={{ minWidth: 70 }}
              >
                Cancel
              </Button>
              <Button size="small" variant="contained" onClick={handleSave} sx={{ minWidth: 70 }}>
                Save
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                mb: 0.5,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 'bold',
                  color: textColor,
                  wordBreak: 'break-word',
                  flexGrow: 1,
                  fontSize: `${calculatedFontSize * 1.1}px`,
                }}
              >
                {note.title || 'Untitled'}
              </Typography>
              {toolMode === TOOL_MODES.CURSOR && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    startEditing();
                  }}
                  sx={{
                    p: 0.5,
                    ml: 0.5,
                    opacity: 0.6,
                    color: isDark ? 'rgba(255,255,255,0.7)' : 'inherit',
                    '&:hover': {
                      opacity: 1,
                      backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                    },
                  }}
                >
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: textColor,
                fontSize: `${calculatedFontSize}px`,
                lineHeight: 1.5,
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
              }}
            >
              {note.content || ''}
            </Typography>

            {/* Related Chapters */}
            {note.relatedChapters && note.relatedChapters.length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mb: 0.5,
                    fontWeight: 'bold',
                    color: textColor,
                    opacity: 0.7,
                    fontSize: `${calculatedFontSize * 0.85}px`,
                  }}
                >
                  Relevant Chapters
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {note.relatedChapters.map((chapter, index) => {
                    const chapterName =
                      typeof chapter === 'string'
                        ? chapter
                        : chapter.name || chapter.title || `Chapter ${index + 1}`;
                    return (
                      <Chip
                        key={`chapter-${chapterName}-${index}`} // eslint-disable-line react/no-array-index-key
                        label={chapterName}
                        size="small"
                        onDelete={
                          toolMode === TOOL_MODES.CURSOR
                            ? () => handleRemoveChapter(index)
                            : undefined
                        }
                        onClick={(e) => {
                          if (toolMode !== TOOL_MODES.CURSOR) {
                            e.stopPropagation();
                          }
                        }}
                        sx={{
                          backgroundColor: `${textColor}15`,
                          color: textColor,
                          fontSize: `${calculatedFontSize * 0.75}px`,
                          fontWeight: 500,
                          cursor: toolMode === TOOL_MODES.CURSOR ? 'pointer' : 'default',
                          '& .MuiChip-deleteIcon': {
                            color: textColor,
                            opacity: 0.7,
                            fontSize: '14px',
                          },
                          '&:hover':
                            toolMode === TOOL_MODES.CURSOR
                              ? {
                                  backgroundColor: `${textColor}25`,
                                }
                              : {},
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Related Characters */}
            {note.relatedCharacters && note.relatedCharacters.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mb: 0.5,
                    fontWeight: 'bold',
                    color: textColor,
                    opacity: 0.7,
                    fontSize: `${calculatedFontSize * 0.85}px`,
                  }}
                >
                  Relevant Characters
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {note.relatedCharacters.map((character, index) => {
                    const characterName =
                      typeof character === 'string'
                        ? character
                        : character.name || character.title || `Character ${index + 1}`;
                    return (
                      <Chip
                        key={`character-${characterName}-${index}`} // eslint-disable-line react/no-array-index-key
                        label={characterName}
                        size="small"
                        onDelete={
                          toolMode === TOOL_MODES.CURSOR
                            ? () => handleRemoveCharacter(index)
                            : undefined
                        }
                        onClick={(e) => {
                          if (toolMode !== TOOL_MODES.CURSOR) {
                            e.stopPropagation();
                          }
                        }}
                        sx={{
                          backgroundColor: `${textColor}15`,
                          color: textColor,
                          fontSize: `${calculatedFontSize * 0.75}px`,
                          fontWeight: 500,
                          cursor: toolMode === TOOL_MODES.CURSOR ? 'pointer' : 'default',
                          '& .MuiChip-deleteIcon': {
                            color: textColor,
                            opacity: 0.7,
                            fontSize: '14px',
                          },
                          '&:hover':
                            toolMode === TOOL_MODES.CURSOR
                              ? {
                                  backgroundColor: `${textColor}25`,
                                }
                              : {},
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Category */}
            {note.category && (
              <Box sx={{ mt: 1 }}>
                <Chip
                  label={note.category}
                  size="small"
                  sx={{
                    backgroundColor: isDark ? 'rgba(100,150,255,0.2)' : 'rgba(25,118,210,0.1)',
                    color: textColor,
                    fontSize: `${calculatedFontSize * 0.7}px`,
                    height: 20,
                  }}
                />
              </Box>
            )}

            {/* Tags */}
            {note.tags && note.tags.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                {note.tags.slice(0, 3).map((tag) => (
                  <Typography
                    key={tag}
                    variant="caption"
                    sx={{
                      backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                      color: textColor,
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 0.5,
                      fontSize: '0.65rem',
                    }}
                  >
                    {tag}
                  </Typography>
                ))}
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Resize Handle - Bottom Right Corner */}
      {toolMode === TOOL_MODES.CURSOR && !isEditing && (
        <Box
          onMouseDown={handleResizeMouseDown}
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: 16,
            height: 16,
            cursor: 'nwse-resize',
            zIndex: 10,
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 2,
              right: 2,
              width: 0,
              height: 0,
              borderStyle: 'solid',
              borderWidth: '0 0 8px 8px',
              borderColor: `transparent transparent ${isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'} transparent`,
            },
          }}
        />
      )}

      {/* Format Menu Popover */}
      <Popover
        open={Boolean(formatMenuAnchor)}
        anchorEl={formatMenuAnchor}
        onClose={() => setFormatMenuAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: {
            backgroundColor: isDark ? '#2d2d2d' : '#ffffff',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          },
        }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography
            variant="caption"
            sx={{ mb: 1, display: 'block', fontWeight: 'bold', color: colors.text.primary }}
          >
            Font Size
          </Typography>
          <Slider
            value={fontSizeMultiplier}
            onChange={handleFontSizeChange}
            min={0.5}
            max={2.0}
            step={0.1}
            marks={[
              { value: 0.5, label: '50%' },
              { value: 1.0, label: '100%' },
              { value: 1.5, label: '150%' },
              { value: 2.0, label: '200%' },
            ]}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
          />
          <Typography
            variant="caption"
            sx={{ mt: 1, display: 'block', color: colors.text.secondary }}
          >
            Current: {Math.round(calculatedFontSize * 10) / 10}px
          </Typography>
        </Box>
      </Popover>

      {/* Background Color Picker Popover */}
      <Popover
        open={Boolean(colorMenuAnchor)}
        anchorEl={colorMenuAnchor}
        onClose={() => setColorMenuAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: {
            backgroundColor: isDark ? '#2d2d2d' : '#ffffff',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          },
        }}
      >
        <Box sx={{ p: 1.5, display: 'flex', flexWrap: 'wrap', width: 200, gap: 0.5 }}>
          {NOTE_COLOR_KEYS.map((colorKey) => {
            const displayColor = resolveColor(colorKey, noteColors, isDark);
            const isSelected = noteColorKey === colorKey;
            return (
              <Box
                key={colorKey}
                onClick={() => handleColorChange(colorKey)}
                title={colorKey}
                sx={{
                  width: 28,
                  height: 28,
                  backgroundColor: displayColor,
                  borderRadius: 0.5,
                  cursor: 'pointer',
                  border: isSelected
                    ? '2px solid #1976d2'
                    : `1px solid ${isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}`,
                  '&:hover': {
                    transform: 'scale(1.1)',
                  },
                  transition: 'transform 0.2s',
                }}
              />
            );
          })}
        </Box>
      </Popover>

      {/* Text Color Picker Popover */}
      <Popover
        open={Boolean(textColorMenuAnchor)}
        anchorEl={textColorMenuAnchor}
        onClose={() => setTextColorMenuAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: {
            backgroundColor: isDark ? '#2d2d2d' : '#ffffff',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          },
        }}
      >
        <Box sx={{ p: 1.5, display: 'flex', flexWrap: 'wrap', width: 200, gap: 0.5 }}>
          {TEXT_COLOR_KEYS.map((colorKey) => {
            const displayColor = resolveColor(colorKey, textColors, isDark);
            const isSelected = textColorKey === colorKey;
            return (
              <Box
                key={colorKey}
                onClick={() => handleTextColorChange(colorKey)}
                title={colorKey}
                sx={{
                  width: 28,
                  height: 28,
                  backgroundColor: displayColor,
                  borderRadius: 0.5,
                  cursor: 'pointer',
                  border: isSelected
                    ? '2px solid #1976d2'
                    : `1px solid ${isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}`,
                  '&:hover': {
                    transform: 'scale(1.1)',
                  },
                  transition: 'transform 0.2s',
                }}
              />
            );
          })}
        </Box>
      </Popover>
    </Paper>
  );
};

CorkboardNote.propTypes = {
  note: PropTypes.shape({
    _id: PropTypes.string,
    id: PropTypes.string,
    noteId: PropTypes.string,
    title: PropTypes.string,
    content: PropTypes.string,
    category: PropTypes.string,
    color: PropTypes.string,
    textColor: PropTypes.string,
    fontSizeMultiplier: PropTypes.number,
    x: PropTypes.number,
    y: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
    zIndex: PropTypes.number,
    tags: PropTypes.arrayOf(PropTypes.string),
    relatedChapters: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({ name: PropTypes.string, title: PropTypes.string }),
      ])
    ),
    relatedCharacters: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({ name: PropTypes.string, title: PropTypes.string }),
      ])
    ),
    relatedNotes: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};

// Memoize component to prevent unnecessary re-renders
// Returns true if props are equal (skip re-render), false if different (re-render)
export default React.memo(CorkboardNote, (prevProps, nextProps) => {
  // Only re-render if these specific properties change
  const prevNote = prevProps.note;
  const nextNote = nextProps.note;

  // Deep compare arrays for relatedChapters and relatedCharacters
  const chaptersEqual =
    JSON.stringify(prevNote.relatedChapters || []) ===
    JSON.stringify(nextNote.relatedChapters || []);
  const charactersEqual =
    JSON.stringify(prevNote.relatedCharacters || []) ===
    JSON.stringify(nextNote.relatedCharacters || []);

  // Return true if props are equal (skip re-render)
  return (
    prevNote._id === nextNote._id &&
    prevNote.x === nextNote.x &&
    prevNote.y === nextNote.y &&
    prevNote.width === nextNote.width &&
    prevNote.height === nextNote.height &&
    prevNote.zIndex === nextNote.zIndex &&
    prevNote.title === nextNote.title &&
    prevNote.content === nextNote.content &&
    prevNote.color === nextNote.color &&
    prevNote.textColor === nextNote.textColor &&
    prevNote.fontSizeMultiplier === nextNote.fontSizeMultiplier &&
    chaptersEqual &&
    charactersEqual
  );
});
